import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { demoReplyForText } from "@/data/aiAssistantDemoAnswers";
import { aiAgentConfigured, askAiAgent, type AiAgentStep } from "@/services/aiAgentApi";
import {
  aiRouteForSection,
  schedulePageContext,
  sectionRoute,
  summaryPageContext,
  type AiRoute,
} from "@/services/aiPageContext";
import {
  AIResult,
  AIMessage,
  AIConversation,
  PopularCommand,
} from "@/types/aiAssistant";
import { aiResultsExamples } from "@/data/aiResultsExamples";
import {
  AudioRecorder,
  speechToText,
  textToSpeech,
  playAudio,
  unlockAudio,
  setLogCallback,
  getApiBaseUrl,
  sendMessage,
  getAIChatAskUrl,
} from "@/services/aiChatService";

// Очистка текста для TTS - убираем скобки и markdown
function stripMarkdown(text: string): string {
  return (
    text
      // Убираем markdown форматирование, оставляя текст
      .replace(/\*\*([^*]+)\*\*/g, "$1") // **bold** → bold
      .replace(/\*([^*]+)\*/g, "$1") // *italic* → italic
      .replace(/__([^_]+)__/g, "$1") // __bold__ → bold
      .replace(/_([^_]+)_/g, "$1") // _italic_ → italic
      // Убираем текст в скобках полностью (не читаем вслух)
      .replace(/\[[^\]]*\]/g, "") // [любой текст] → ""
      .replace(/\([^)]*\)/g, "") // (любой текст) → ""
      // Очистка пробелов
      .replace(/\s{2,}/g, " ") // множественные пробелы → один
      .trim()
  );
}

// Популярные команды
export const popularCommands: PopularCommand[] = [
  {
    id: "schedule_today",
    label: "График на сегодня",
    command: "Проверь мой график на сегодня",
    description: "Свободное время",
    requiresConfirmation: false,
  },
  {
    id: "schedule_tomorrow",
    label: "График на завтра",
    command: "Проверь мой график на завтра",
    description: "Свободное время",
    requiresConfirmation: false,
  },
  {
    id: "quick_summary",
    label: "Краткая сводка",
    command: "Дай краткую сводку по всем разделам",
    description: "Все направления",
    requiresConfirmation: false,
  },
  {
    /*
     * Был «Топ проблемных регионов» — наследство республиканского продукта:
     * у областной панели регион один. Сервис умеет другое — сроки и решения
     * органов области по реестру е-Өтініш (`control`), это и спрашиваем.
     */
    id: "top_regions",
    label: "Топ проблемных органов",
    command: "Какие органы Атырауской области чаще всего нарушают сроки рассмотрения обращений?",
    description: "Сроки и решения",
    requiresConfirmation: false,
  },
  {
    // Была «Астана» — наследство республиканского продукта. Областная панель
    // спрашивает про свою область.
    id: "crime_report_atyrau",
    label: "Справка: правопорядок",
    command: "Сделай справку по общественной безопасности в Атырауской области",
    description: "КУИ и адм. нарушения",
    requiresConfirmation: false,
  },
  {
    /*
     * Была «защита бизнеса: проверки и жалобы» — этих данных у Атырау нет.
     * Есть инвестпроекты НЦИП — раздел «Бизнес и инвесторы».
     */
    id: "business_report",
    label: "Справка: инвестпроекты",
    command: "Подготовь справку по инвестиционным проектам области",
    description: "Ход реализации",
    requiresConfirmation: false,
  },
  {
    id: "appeals_report",
    label: "Справка: обращения граждан",
    command: "Подготовь справку по обращениям граждан",
    description: "Аналитика обращений",
    requiresConfirmation: false,
  },
];

/*
 * КУДА ИДЁТ БЫСТРАЯ КОМАНДА — не туда, где открыт ассистент. «График на
 * завтра» с экрана труда должен уйти с графиком, а не со срезом ВЛИ. Поэтому у
 * каждой команды свой маршрут; набранный руками вопрос идёт по разделу.
 */
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d;
};

const QUICK_ROUTES: Record<string, () => AiRoute> = {
  schedule_today: () => ({
    domain: "page",
    load: async () => schedulePageContext(new Date(), "График на сегодня"),
  }),
  schedule_tomorrow: () => ({
    domain: "page",
    load: async () => schedulePageContext(tomorrow(), "График на завтра"),
  }),
  quick_summary: () => ({ domain: "page", load: summaryPageContext }),
  top_regions: () => ({ domain: "control" }),
  crime_report_atyrau: () => sectionRoute("public-safety")!,
  business_report: () => sectionRoute("business-investors")!,
  appeals_report: () => ({ domain: "otinish" }),
};

function quickRouteFor(text: string): AiRoute | null {
  const cmd = popularCommands.find((c) => c.command === text.trim());
  return cmd ? (QUICK_ROUTES[cmd.id]?.() ?? null) : null;
}

// Тип для дебаг логов
export interface DebugLog {
  id: string;
  timestamp: Date;
  type: "info" | "success" | "error" | "warning";
  message: string;
  data?: any;
}

export function useAIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false); // STT распознавание
  const [isThinking, setIsThinking] = useState(false); // AI обработка
  const [isSpeaking, setIsSpeaking] = useState(false); // TTS воспроизведение

  // Для обратной совместимости
  const isProcessing = isTranscribing || isThinking;
  const [currentConversation, setCurrentConversation] =
    useState<AIConversation | null>(null);
  const [results, setResults] = useState<AIResult[]>([]);
  const [debugLogs, setDebugLogs] = useState<DebugLog[]>([]);
  const [showDebug, setShowDebug] = useState(false);

  /*
   * Идентификатор разговора с агентом: с ним «а теперь разбей по районам»
   * понимается как продолжение, без него каждый вопрос начинается с нуля.
   * Разговор привязан к одному домену — при смене домена сбрасывать.
   */
  const agentConversationRef = useRef<string | null>(null);
  /** Домен (и экран для `page`), к которому относится `agentConversationRef`. */
  const agentConversationKeyRef = useRef<string>("");
  /** Что агент делает прямо сейчас — подпись шага вместо общей фразы ожидания. */
  const [agentStep, setAgentStep] = useState<string | null>(null);

  // Раздел берётся из адреса: ассистент открывается поверх любого экрана.
  const location = useLocation();
  const section = location.pathname.split("/").filter(Boolean)[0] || "base";
  const sectionRef = useRef(section);
  sectionRef.current = section;
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  // Функция для добавления дебаг лога
  const addLog = useCallback(
    (type: DebugLog["type"], message: string, data?: any) => {
      const log: DebugLog = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date(),
        type,
        message,
        data,
      };
      setDebugLogs((prev) => [...prev.slice(-50), log]); // Храним последние 50 логов
    },
    []
  );

  // Очистка логов
  const clearLogs = useCallback(() => {
    setDebugLogs([]);
  }, []);

  // Toggle debug panel
  const toggleDebug = useCallback(() => {
    setShowDebug((prev) => !prev);
  }, []);

  // Остановка распознавания речи (STT)
  const stopTranscribing = useCallback(() => {
    addLog("warning", "🛑 Остановка STT по запросу пользователя");
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsTranscribing(false);
  }, [addLog]);

  // Остановка AI обработки (Chat)
  const stopThinking = useCallback(() => {
    addLog("warning", "🛑 Остановка AI по запросу пользователя");
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsThinking(false);
  }, [addLog]);

  // Остановка воспроизведения (TTS)
  const stopSpeaking = useCallback(() => {
    addLog("warning", "🛑 Остановка TTS по запросу пользователя");
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      audioElementRef.current = null;
    }
    // Также остановим через AudioContext если используется
    if (typeof window !== "undefined" && (window as any).__currentAudioSource) {
      try {
        (window as any).__currentAudioSource.stop();
        (window as any).__currentAudioSource = null;
      } catch (e) {
        // ignore
      }
    }
    setIsSpeaking(false);
  }, [addLog]);

  // Устанавливаем callback для логирования из aiChatService
  useEffect(() => {
    setLogCallback((type, message, data) => {
      addLog(type, message, data);
    });

    return () => {
      setLogCallback(null);
    };
  }, [addLog]);

  // Инициализация с примерами результатов (для демонстрации)
  useEffect(() => {
    // В реальном приложении здесь будет загрузка из localStorage или API
    const savedResults = localStorage.getItem("ai-results");
    if (savedResults) {
      try {
        const parsed = JSON.parse(savedResults);
        // Конвертируем даты обратно
        const resultsWithDates = parsed.map((r: any) => ({
          ...r,
          timestamp: new Date(r.timestamp),
        }));
        setResults(resultsWithDates);
      } catch (e) {
        // Если ошибка парсинга, используем примеры
        setResults(aiResultsExamples);
      }
    } else {
      // Первый запуск - используем примеры
      setResults(aiResultsExamples);
    }
  }, []);

  // Сохранение результатов в localStorage при изменении
  useEffect(() => {
    if (results.length > 0) {
      localStorage.setItem("ai-results", JSON.stringify(results));
    }
  }, [results]);

  // Начать прослушивание (запись с микрофона)
  const startListening = useCallback(async () => {
    addLog("info", "🎤 [1] Клик на микрофон - startListening()");

    // Разблокируем аудио при первом клике (для TTS)
    unlockAudio();
    addLog("info", "🔓 Аудио разблокировано для TTS");

    // Проверяем поддержку MediaRecorder
    addLog("info", "🔍 [2] Проверка поддержки MediaRecorder...");
    if (!AudioRecorder.isSupported()) {
      addLog("error", "❌ MediaRecorder НЕ поддерживается!");
      alert("Запись аудио не поддерживается в вашем браузере.");
      return;
    }
    addLog("success", "✅ [2] MediaRecorder поддерживается");

    try {
      // Создаем новый рекордер
      addLog("info", "🔧 [3] Создание AudioRecorder...");
      audioRecorderRef.current = new AudioRecorder();

      // Запрашиваем разрешение и начинаем запись
      addLog("info", "🎙️ [4] Запрос доступа к микрофону...");
      await audioRecorderRef.current.start();
      setIsListening(true);
      addLog("success", "✅ [4] Запись НАЧАТА! Говорите...");
    } catch (error: any) {
      addLog("error", "❌ [4] Ошибка запуска записи", {
        error: error?.message || String(error),
      });
      alert(
        "Не удалось получить доступ к микрофону.\n\nПожалуйста, разрешите доступ в настройках браузера."
      );
      setIsListening(false);
    }
  }, [addLog]);

  // Остановить прослушивание и отправить на распознавание
  const stopListening = useCallback(async () => {
    addLog("info", "🛑 [5] Повторный клик - stopListening()");

    if (!audioRecorderRef.current || !isListening) {
      addLog("warning", "⚠️ [5] Рекордер не активен, пропускаем");
      setIsListening(false);
      return;
    }

    setIsListening(false);
    setIsTranscribing(true);

    // Создаем AbortController для возможности отмены
    abortControllerRef.current = new AbortController();

    try {
      // Получаем записанное аудио
      addLog("info", "📦 [6] Остановка записи...");
      const audioBlob = await audioRecorderRef.current.stop();
      addLog("success", "✅ [6] Аудио получено", {
        size: audioBlob.size + " bytes",
        type: audioBlob.type,
      });

      // Отправляем на STT API
      addLog("info", `📤 [7] Отправка на STT API → ${getApiBaseUrl()}/stt/v2`);
      const transcript = await speechToText(audioBlob, "ru", {
        signal: abortControllerRef.current.signal,
      });
      addLog("success", "✅ [7] Ответ STT API", { transcript });

      setIsTranscribing(false);

      if (transcript && transcript.trim()) {
        addLog("info", "💬 [8] Передача текста в handleUserInput()...");
        handleUserInput(transcript, true);
      } else {
        addLog("warning", "⚠️ [7] Пустой результат распознавания");
      }
    } catch (error: any) {
      if (error?.name === "AbortError") {
        addLog("warning", "⚠️ STT запрос отменен пользователем");
      } else {
        const sttUrl = `${getApiBaseUrl()}/stt/v2`;
        addLog("error", `❌ [7] Ошибка STT → ${sttUrl}`, {
          fullUrl: sttUrl,
          error: error?.message || String(error),
          errorName: error?.name,
          errorStack: error?.stack?.split("\n").slice(0, 3).join(" | "),
        });
        alert(
          `Не удалось распознать речь.\n\nURL: ${sttUrl}\nОшибка: ${
            error?.message || String(error)
          }`
        );
      }
    } finally {
      setIsTranscribing(false);
      abortControllerRef.current = null;
      audioRecorderRef.current?.cleanup();
      audioRecorderRef.current = null;
      addLog("info", "🧹 Ресурсы очищены");
    }
  }, [isListening, addLog]);

  // Обработка пользовательского ввода
  const handleUserInput = useCallback(
    async (input: string, isVoice: boolean = false) => {
      addLog("info", "💬 [8] handleUserInput() - обработка текста", {
        input,
        isVoice,
      });

      if (!input.trim()) {
        addLog("warning", "⚠️ Пустой текст, пропускаем");
        return;
      }

      // Добавляем сообщение пользователя
      addLog("info", "💾 [9] Добавление сообщения в чат...");
      const userMessage: AIMessage = {
        id: Date.now().toString(),
        role: "user",
        content: input,
        timestamp: new Date(),
        isVoice: isVoice,
      };

      // Обновляем или создаем разговор
      setCurrentConversation((prev) => {
        const conversation: AIConversation = prev || {
          id: Date.now().toString(),
          messages: [],
          results: [],
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        return {
          ...conversation,
          messages: [...conversation.messages, userMessage],
          updatedAt: new Date(),
        };
      });
      addLog("success", "✅ [9] Сообщение добавлено");

      setIsThinking(true);
      addLog("info", "🤖 [10] Отправка запроса на AI API...");

      // Создаем AbortController для возможности отмены
      abortControllerRef.current = new AbortController();

      try {
        /*
         * ЖИВОЙ АГЕНТ, ЕСЛИ ОН НАСТРОЕН, ИНАЧЕ ПРЕЖНЯЯ РУЧКА.
         *
         * `sendMessage` бьёт в `sequence-ai/ask` на нашем API — эта ручка
         * осталась от республиканского продукта, у бэкенда Атырау её нет.
         *
         * ДОМЕН — ПО РАЗДЕЛУ (`aiRouteForSection`) или по быстрой команде.
         * Срез экрана для `page` читается ЗДЕСЬ, в момент отправки: человек мог
         * сменить район между вопросами.
         *
         * ОТВЕТ ЖДЁМ ДО МИНУТЫ, и это нормально: подпись шага видна в окне.
         */
        let steps: AiAgentStep[] = [];
        let answer: string;
        if (aiAgentConfigured()) {
          const route = quickRouteFor(input) ?? aiRouteForSection(sectionRef.current, input);
          const context = route.domain === "page" ? await route.load() : null;
          // Разговор привязан к домену и экрану: сервис их не смешивает, и
          // id из другого среза он молча заменил бы новым разговором.
          const key = `${route.domain}:${context?.page.id ?? ""}`;
          if (agentConversationKeyRef.current !== key) {
            agentConversationRef.current = null;
            agentConversationKeyRef.current = key;
          }
          addLog("info", `🤖 Домен агента: ${key}`);
          const res = await askAiAgent(
            {
              domain: route.domain,
              message: input,
              conversation_id: agentConversationRef.current,
              context,
            },
            {
              signal: abortControllerRef.current.signal,
              onStep: (label) => {
                setAgentStep(label);
                addLog("info", `🤖 ${label}…`);
              },
            },
          );
          agentConversationRef.current = res.conversationId;
          steps = res.steps;
          answer = res.text;
        } else {
          answer = await sendMessage(input, {
            signal: abortControllerRef.current.signal,
          });
        }

        addLog("success", "✅ [10] AI ответил", {
          text: answer.substring(0, 100) + (answer.length > 100 ? "..." : ""),
        });

        // Добавляем сообщение ассистента с ответом
        const assistantMessage: AIMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: answer,
          timestamp: new Date(),
          // «Откуда это число» — только у ответа самого агента.
          ...(steps.length ? { steps } : {}),
        };

        setCurrentConversation((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            messages: [...prev.messages, assistantMessage],
            updatedAt: new Date(),
          };
        });

        setIsThinking(false);

        // 🔊 Запускаем TTS только если пользователь отправил голосовое сообщение
        if (answer.trim() && isVoice) {
          // Очищаем markdown перед озвучкой
          const cleanText = stripMarkdown(answer);
          addLog(
            "info",
            `🔊 [11] TTS → ${getApiBaseUrl()}/tts/v2 (голосовой режим)`,
            {
              originalLength: answer.length,
              cleanLength: cleanText.length,
            }
          );
          setIsSpeaking(true);

          textToSpeech(cleanText)
            .then(async (audioBlob) => {
              addLog("success", "✅ TTS аудио получено", {
                size: audioBlob.size + " bytes",
                type: audioBlob.type,
              });
              addLog("info", "▶️ Воспроизведение аудио...");
              // Сохраняем ссылку на текущий источник для возможности остановки
              await playAudio(audioBlob);
              addLog("success", "✅ Воспроизведение завершено!");
            })
            .catch((error: any) => {
              if (error?.name === "AbortError") {
                addLog("warning", "⚠️ TTS остановлен пользователем");
              } else {
                const ttsUrl = `${getApiBaseUrl()}/tts/v2`;
                addLog("error", `❌ TTS ошибка → ${ttsUrl}`, {
                  fullUrl: ttsUrl,
                  error: error?.message || String(error),
                  errorName: error?.name,
                });
              }
            })
            .finally(() => {
              setIsSpeaking(false);
            });
        } else if (answer.trim()) {
          addLog("info", "📝 Текстовый режим - TTS пропущен");
        }
      } catch (error: any) {
        if (error?.name === "AbortError") {
          addLog("warning", "⚠️ AI запрос отменен пользователем");
        } else {
          // Адрес — тот, куда запрос ушёл на самом деле: агент или старая ручка.
          const failedUrl = aiAgentConfigured() ? "/ai/chat" : getAIChatAskUrl();
          addLog("error", `❌ [10] Ошибка AI API → ${failedUrl}`, {
            fullUrl: failedUrl,
            error: error?.message || String(error),
            errorName: error?.name,
            errorStack: error?.stack?.split("\n").slice(0, 3).join(" | "),
          });

          /*
           * ЗАГОТОВКА ВМЕСТО ТЕКСТА ОШИБКИ, ЕСЛИ АГЕНТ НЕ ОТВЕТИЛ.
           *
           * Сервис виден только из офисной сети: с прода `/ai` отвечает 504, да
           * и в офисе модель бывает занята или перезапускается. Показывать
           * вместо ответа «Ошибка: 504» — значит выдавать недоступность сети за
           * поломку панели.
           *
           * Заготовка ПОМЕЧЕНА как заготовка (см. `aiAssistantDemoAnswers`), и
           * числа в ней сняты с живых реестров, поэтому она не спорит с
           * экранами. Причина отказа — в журнале отладки.
           */
          const demo = demoReplyForText(input);
          const errorMessage: AIMessage = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content:
              demo?.text ??
              `Не удалось получить ответ: ${
                error?.message || "сервис недоступен"
              }.`,
            timestamp: new Date(),
            // Три уточняющих вопроса под ответом: разговор из одной реплики
            // показывает справочник, а не ассистента.
            followUps: demo?.followUps,
          };

          setCurrentConversation((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              messages: [...prev.messages, errorMessage],
              updatedAt: new Date(),
            };
          });
        }
      } finally {
        setIsThinking(false);
        setAgentStep(null);
        abortControllerRef.current = null;
      }
    },
    [addLog]
  );

  const openAssistant = useCallback(() => {
    setIsOpen(true);
    if (!currentConversation) {
      setCurrentConversation({
        id: Date.now().toString(),
        messages: [],
        results: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
  }, [currentConversation]);

  const closeAssistant = useCallback(() => {
    setIsOpen(false);
    stopListening();
  }, [stopListening]);

  const deleteResult = useCallback((id: string) => {
    setResults((prev) => {
      const updated = prev.filter((r) => r.id !== id);
      localStorage.setItem("ai-results", JSON.stringify(updated));
      return updated;
    });
  }, []);

  return {
    isOpen,
    isListening,
    isProcessing,
    isTranscribing,
    isThinking,
    agentStep,
    isSpeaking,
    currentConversation,
    results,
    openAssistant,
    closeAssistant,
    startListening,
    stopListening,
    handleUserInput,
    deleteResult,
    popularCommands,
    // Функции остановки
    stopTranscribing,
    stopThinking,
    stopSpeaking,
    // Debug
    debugLogs,
    showDebug,
    toggleDebug,
    clearLogs,
    // API Mode
  };
}
